import { useCallback, useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { buildSmsHref, formatPhone } from '../../../shared-web/onboarding-phone';
import { isInAppWebview } from '../../../shared-web/ua-detect';
import { jumpToOriLane } from '../../../shared-web/ori-lane-jump';
import { buildVerifyMessage } from '../../../shared-web/onboarding-message';
import { OriApiError, getOnboardingStatus, startOnboarding, exchangeOnboardingForSession } from '../lib/oriApi';
import { getStoredChannel, getStoredAttribution, getStoredTtclid, getStoredFbclid } from '../lib/channel';
import { trackTikTokContact } from '../lib/tiktok';
import { trackMetaContact } from '../lib/meta';
import { logEvent } from '../telemetry/log-event';
import { useAsyncAction } from '../../../shared-web/useAsyncAction';

type Stage = 'form' | 'handoff' | 'verified' | 'expired';

// After the sms: deep link fires we can't know for sure it opened Messages, so
// we infer it: 'pending' = just fired, watching; 'opened' = the tab got
// backgrounded (app launched), keep the calm "you're sent?" guidance; 'failed'
// = still here after a beat (desktop, or a blocked in-app webview) → show the
// copyable number + message fallback.
type HandoffOutcome = 'pending' | 'opened' | 'failed';

const sans = '"STK Bureau Sans", Inter, system-ui, sans-serif';
const serif = '"Terza Display", "Iowan Old Style", Georgia, serif';

// One conversion signal, two sinks: TikTok and Meta each get a uniform `Contact`
// event (the ad-side optimization goal), while our own /api/web-events row keeps
// the ad click markers (ttclid / fbclid / utm_source) so the admin DB can
// segment ad-driven clicks. Fired the moment we hand off to Messages.
function trackOpenMessages(): void {
  trackTikTokContact();
  trackMetaContact();
  const attrib = getStoredAttribution();
  logEvent('marketing.handoff_click', {
    target: 'open_messages',
    channel: getStoredChannel() || null,
    utm_source: attrib?.utm?.source || null,
    ttclid: getStoredTtclid() || null,
    fbclid: getStoredFbclid() || null,
    is_returning: false,
  });
}

export default function MessageOriModal({
  onClose,
  mode = 'message',
  returningNumber = '',
}: {
  onClose: () => void;
  // 'message'/'verify' are the SAME one-tap onboarding screen (consent + a single
  // CTA that mints the code and jumps to Messages); they differ only in copy:
  //   'message' = Hero "Message Ori" join funnel.
  //   'verify'  = dashboard sign-in, reframed as identity verification
  //               (opened by ?from=dashboard when signed out).
  // 'returning' = already signed in: skip onboarding entirely and deep-link back
  //               into the existing Ori thread (uses returningNumber).
  mode?: 'message' | 'verify' | 'returning';
  // The signed-in viewer's own Ori number, only set for mode 'returning'.
  returningNumber?: string;
}) {
  const [stage, setStage] = useState<Stage>('form');
  // Idempotency nonce for /start — regenerated only when the user restarts
  // after an expired code, so a double-tap reuses the same code + number.
  const [clientToken, setClientToken] = useState(() => crypto.randomUUID());
  const [name, setName] = useState<string>('');
  const [over18, setOver18] = useState(false);
  const [consent, setConsent] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [handoff, setHandoff] = useState<{ to_number: string; greeting: string; session_id: string } | null>(null);
  const [handoffOutcome, setHandoffOutcome] = useState<HandoffOutcome>('pending');
  // Cleanup for the in-flight deep-link watcher (listeners + timer), so a
  // restart or unmount can't leak it.
  const watchCleanupRef = useRef<(() => void) | null>(null);
  // Resolved from /status on completion — the user's REAL Ori lane number
  // (the handshake number can be a throwaway).
  const [verifiedNumber, setVerifiedNumber] = useState('');

  // Dismissing the sheet. If the user signed in during this flow (stage hit
  // 'verified', so the session cookie is set), don't just hide the sheet on top
  // of the sticky ?from=dashboard URL — navigate to a fresh page so the nav and
  // dashboard reflect the new session. Otherwise close normally.
  const handleDismiss = useCallback(() => {
    if (stage === 'verified') {
      let fromDashboard = false;
      try {
        fromDashboard = new URLSearchParams(window.location.search).get('from') === 'dashboard';
      } catch {
        fromDashboard = false;
      }
      window.location.href = fromDashboard ? '/me' : '/';
      return;
    }
    onClose();
  }, [stage, onClose]);

  // Lock body scroll + Escape to close
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handleDismiss();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [handleDismiss]);

  // Poll the session status once a code is armed so this tab flips to "you're in"
  // (or straight to /me for dashboard) the moment the SMS lands — otherwise the
  // tab stares at the join screen forever with no confirmation it worked.
  const handoffSessionId = stage === 'handoff' ? handoff?.session_id || '' : '';
  useEffect(() => {
    if (!handoffSessionId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const poll = async () => {
      try {
        const r = await getOnboardingStatus(handoffSessionId);
        if (cancelled) return;
        if (r?.status === 'completed') {
          // Trade the completed verify session for a browser session cookie so
          // the site knows you're signed in (Nav pill + /me).
          const signedIn = await exchangeOnboardingForSession(handoffSessionId);
          if (cancelled) return;
          // Dashboard login: skip the success screen entirely — once the cookie
          // is saved (awaited above) go straight to the dashboard.
          let fromDashboard = false;
          try {
            fromDashboard = new URLSearchParams(window.location.search).get('from') === 'dashboard';
          } catch {
            fromDashboard = false;
          }
          if (fromDashboard && signedIn) {
            window.location.href = '/me';
            return;
          }
          setVerifiedNumber((String(r?.ori_number || '').trim() || handoff?.to_number || '').trim());
          setStage('verified');
          return;
        }
        if (r?.status === 'expired') {
          setStage('expired');
          return;
        }
      } catch {
        // transient / not yet visible — keep polling while the modal is open
      }
      if (!cancelled) timer = setTimeout(() => { void poll(); }, 3000);
    };
    timer = setTimeout(() => { void poll(); }, 3000);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [handoffSessionId, handoff?.to_number]);

  // Tear down any in-flight deep-link watcher.
  const clearWatch = useCallback(() => {
    if (watchCleanupRef.current) {
      watchCleanupRef.current();
      watchCleanupRef.current = null;
    }
  }, []);

  // Drop the watcher when the modal unmounts so timers/listeners don't leak.
  useEffect(() => clearWatch, [clearWatch]);

  // Expired code → restart with a FRESH idempotency nonce (same form data),
  // so /start mints a new code instead of replaying the dead one.
  function handleRestart() {
    clearWatch();
    setClientToken(crypto.randomUUID());
    setHandoff(null);
    setHandoffOutcome('pending');
    setBanner(null);
    setStage('form');
  }

  // Mint a code via /start and arm the handoff (number + greeting + session) so
  // the poller can flip this tab to signed-in. Returns the sms: deep link to the
  // verify text, or null if the call failed (banner already set).
  async function startAndSetHandoff(): Promise<string | null> {
    setBanner(null);
    try {
      const acceptedAt = new Date();
      const attrib = getStoredAttribution();
      // PostHog's anonymous browser id for THIS session — the same distinct_id
      // stamped on the pre-onboarding clickstream (ori_web_ui_events). Threading
      // it through the handoff lets the admin user-journey panel stitch this
      // person's anonymous website path onto their actor after onboarding.
      let distinctId: string | undefined;
      try {
        distinctId = posthog?.get_distinct_id?.() || undefined;
      } catch {
        distinctId = undefined;
      }
      const r = await startOnboarding({
        name: name.trim(),
        ageConfirmed: true,
        acceptedAt,
        clientToken,
        channel: getStoredChannel(),
        painpoint: attrib?.painpoint,
        platform: attrib?.platform,
        solution: attrib?.solution,
        utm: attrib?.utm,
        distinctId,
      });

      const greeting = buildVerifyMessage({ code: r.verification.code, attestAge: true, acceptedAt });
      setHandoff({
        to_number: r.verification.to_number,
        greeting,
        session_id: r.onboarding_session_id,
      });
      setStage('handoff');
      return buildSmsHref(r.verification.to_number, greeting);
    } catch (err: unknown) {
      const code = err instanceof OriApiError ? err.code : undefined;
      switch (code) {
        case 'name_required':
          setBanner('Please tell us what to call you.');
          break;
        case 'age_confirmation_required':
          setBanner('Please confirm you are 18 or older.');
          break;
        case 'pool_empty':
          setBanner("We're at capacity right now — try again in a few minutes.");
          break;
        default:
          setBanner('Network error — please try again.');
      }
      return null;
    }
  }

  // One tap: mint the code AND jump straight to Messages with it filled in — no
  // intermediate card, in either mode. The handoff stays armed, so when the user
  // sends the text and returns, the poller signs them in (→ /me from dashboard,
  // or the "you're in" screen otherwise). We only reveal the copyable number +
  // message fallback if the jump is detected to have failed (desktop, or a
  // blocked in-app webview) — see watchHandoff.
  const { run: handleOpen, pending: verifying } = useAsyncAction(async () => {
    if (!canSubmit) return;
    setHandoffOutcome('pending');
    const href = await startAndSetHandoff();
    if (!href) return;
    trackOpenMessages();
    // Shared handoff: on Windows/Linux (sms: launches nothing) this reveals the
    // copyable card immediately; on iOS/Android/macOS it fires the deep link and
    // the watcher flips to 'failed' if it no-ops. (See shared-web/ori-lane-jump.)
    clearWatch();
    watchCleanupRef.current = jumpToOriLane(href, {
      onOpened: () => setHandoffOutcome('opened'),
      onFallback: () => setHandoffOutcome('failed'),
    });
  });

  const canSubmit = Boolean(name.trim()) && over18 && consent && !verifying;

  return (
    <div
      className="ori-modal-backdrop fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      onClick={handleDismiss}
      role="dialog"
      aria-modal="true"
      aria-labelledby="message-ori-title"
    >
      <div
        className="ori-modal-sheet relative max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-xl sm:max-w-[480px] sm:rounded-2xl"
        style={{ fontFamily: sans }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={handleDismiss}
          className="ori-modal-close absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-ink/70 hover:bg-ink/5 hover:text-ink"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {banner && (
          <div
            className="mx-6 mt-6 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
            role="alert"
          >
            {banner}
          </div>
        )}

        {mode === 'returning' ? (
          <ReturningStage oriNumber={returningNumber} />
        ) : stage === 'verified' ? (
          <VerifiedStage oriNumber={verifiedNumber || handoff?.to_number || ''} />
        ) : stage === 'expired' ? (
          <ExpiredStage onRestart={handleRestart} />
        ) : (
          <JoinStage
            mode={mode}
            name={name}
            setName={setName}
            over18={over18}
            setOver18={setOver18}
            consent={consent}
            setConsent={setConsent}
            canSubmit={canSubmit}
            verifying={verifying}
            onOpen={handleOpen}
            handoff={handoff}
            outcome={handoffOutcome}
            onReveal={() => { clearWatch(); setHandoffOutcome('failed'); }}
          />
        )}
      </div>
    </div>
  );
}

// Name + age + ToS/marketing consent — shared by both modes.
function ConsentFields({
  name,
  setName,
  over18,
  setOver18,
  consent,
  setConsent,
}: {
  name: string;
  setName: (v: string) => void;
  over18: boolean;
  setOver18: (v: boolean) => void;
  consent: boolean;
  setConsent: (v: boolean) => void;
}) {
  return (
    <div className="ori-consent-fields">
      <Field label="What should Ori call you?">
        <input
          type="text"
          autoComplete="name"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Your name"
          className="ori-modal-input w-full rounded-lg border border-ink/15 bg-white px-3 py-2.5 text-sm focus:border-oriOrange focus:outline-none focus:ring-2 focus:ring-oriOrange/20"
        />
      </Field>

      <div className="mb-3 mt-2 flex items-center gap-3">
        <input
          id="ori-over18"
          type="checkbox"
          checked={over18}
          onChange={(e) => setOver18(e.target.checked)}
          className="ori-modal-checkbox h-5 w-5 shrink-0 cursor-pointer accent-oriOrange"
        />
        <label
          htmlFor="ori-over18"
          className="ori-consent-label cursor-pointer select-none text-sm leading-5 text-ink/80"
        >
          I&rsquo;m 18 or older.
        </label>
      </div>

      <div className="mb-6 flex items-center gap-3">
        <input
          id="ori-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="ori-modal-checkbox h-5 w-5 shrink-0 cursor-pointer accent-oriOrange"
        />
        <label
          htmlFor="ori-consent"
          className="ori-consent-label cursor-pointer select-none text-sm leading-5 text-ink/80"
        >
          I agree to Ori&rsquo;s{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-oriOrange underline"
          >
            Terms
          </a>
          ,{' '}
          <a
            href="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-oriOrange underline"
          >
            Privacy Policy
          </a>
          , and to receive transactional and marketing texts.
        </label>
      </div>
    </div>
  );
}

// The generic green "Open Messages" CTA — shared by every handoff surface.
// With href → real <a> sms: link (reliable native gesture). Without href →
// <button> whose onClick mints the code then redirects.
function OpenMessagesCta({
  href,
  onClick,
  primary,
  secondary,
  className = 'mt-5',
  disabled = false,
}: {
  href?: string;
  onClick?: () => void;
  primary: string;
  secondary: string;
  className?: string;
  disabled?: boolean;
}) {
  const inner = (
    <>
      <span
        className="flex shrink-0 items-center justify-center"
        style={{
          width: 38,
          height: 38,
          borderRadius: 11,
          background: '#34C759',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.24)',
        }}
        aria-hidden
      >
        <ChatBubbleGlyph />
      </span>
      <span className="flex flex-1 flex-col text-left" style={{ gap: 2 }}>
        <span style={{ fontSize: 15, fontWeight: 600 }}>{primary}</span>
        <span style={{ fontSize: 11, fontWeight: 400, color: 'rgba(255,255,255,0.78)' }}>
          {secondary}
        </span>
      </span>
      <span style={{ fontSize: 18, color: 'rgba(255,255,255,0.85)', marginLeft: 4 }} aria-hidden>
        →
      </span>
    </>
  );
  const style = {
    gap: 12,
    padding: '12px 14px',
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.07)',
    background: 'linear-gradient(180deg, #302a25 0%, #211d19 100%)',
    color: '#fffdf9',
    boxShadow: '0 10px 26px rgba(43,37,32,0.18), inset 0 1px 0 rgba(255,255,255,0.10)',
    fontFamily: sans,
    transitionDuration: '160ms',
  } as const;
  const cls = `${className} ori-open-messages flex w-full items-center no-underline transition-transform active:scale-[0.99]`;

  if (href) {
    return (
      <a href={href} onClick={onClick} className={cls} style={style}>
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${cls} disabled:cursor-not-allowed disabled:opacity-50`}
      style={style}
    >
      {inner}
    </button>
  );
}

// The single join screen for both modes: consent fields above one CTA. Tapping
// the CTA mints the code and jumps straight to Messages (no intermediate card).
// Copy is the only thing that differs between the Message-Ori funnel and the
// dashboard verify sign-in.
function JoinStage(p: {
  mode: 'message' | 'verify';
  name: string;
  setName: (v: string) => void;
  over18: boolean;
  setOver18: (v: boolean) => void;
  consent: boolean;
  setConsent: (v: boolean) => void;
  canSubmit: boolean;
  verifying: boolean;
  onOpen: () => void;
  handoff: { to_number: string; greeting: string } | null;
  outcome: HandoffOutcome;
  onReveal: () => void;
}) {
  const isVerify = p.mode === 'verify';
  const ctaSecondary = isVerify ? 'your sign-in code is ready' : 'your first message is ready';

  // Code minted and the deep link fired. Until we detect it failed, keep a calm
  // "Messages is opening" panel (no raw number) — the user is meant to be in
  // Messages sending the text. Only when the jump is detected to have no-op'd
  // (desktop, or a blocked in-app webview) do we reveal the copyable fallback.
  if (p.handoff) {
    return p.outcome === 'failed'
      ? <HandoffFallback handoff={p.handoff} ctaSecondary={ctaSecondary} />
      : <HandoffWaiting handoff={p.handoff} onReveal={p.onReveal} />;
  }

  const title = isVerify ? 'Verify it’s you' : 'Message Ori';
  const subtitle = isVerify
    ? 'Confirm a couple of details, then open Messages with your sign-in code ready to send.'
    : 'Tell us a little about you, then open Messages with your first text to Ori ready to send.';

  return (
    <div className="ori-modal-content px-6 pb-8 pt-10 sm:px-8">
      <h2
        id="message-ori-title"
        className="ori-modal-title mb-2 text-3xl font-medium tracking-tight"
        style={{ fontFamily: serif, color: '#3A331D' }}
      >
        {title}
      </h2>
      <p className="ori-modal-subtitle mb-6 text-sm text-ink/70">{subtitle}</p>

      <ConsentFields
        name={p.name}
        setName={p.setName}
        over18={p.over18}
        setOver18={p.setOver18}
        consent={p.consent}
        setConsent={p.setConsent}
      />

      <OpenMessagesCta
        onClick={p.onOpen}
        disabled={!p.canSubmit}
        primary={p.verifying ? 'Opening Messages…' : 'Open Messages'}
        secondary={p.verifying ? 'getting your code' : ctaSecondary}
      />
    </div>
  );
}

// Mobile success path: the deep link fired and (so far) the tab didn't come
// back, so the user should be in Messages. Keep it calm and guidance-first —
// no raw number — but leave a manual retry plus an escape hatch to the
// copyable fallback in case detection was wrong.
function HandoffWaiting(p: {
  handoff: { to_number: string; greeting: string };
  onReveal: () => void;
}) {
  return (
    <div className="ori-modal-content px-6 pb-8 pt-10 sm:px-8">
      <h2
        id="message-ori-title"
        className="ori-modal-title mb-2 text-3xl font-medium tracking-tight"
        style={{ fontFamily: serif, color: '#3A331D' }}
      >
        Opening Messages…
      </h2>
      <p className="ori-modal-subtitle mb-6 text-sm text-ink/70">
        Send the prefilled text to Ori, then come back here — you&rsquo;ll be signed
        in automatically. From here on, everything with Ori happens in Messages, so
        keep the conversation going right there.
      </p>

      <OpenMessagesCta
        href={buildSmsHref(p.handoff.to_number, p.handoff.greeting)}
        onClick={trackOpenMessages}
        primary="Open Messages"
        secondary="tap if it didn’t open"
        className="mt-1"
      />

      <button
        type="button"
        onClick={p.onReveal}
        className="mt-4 w-full text-center text-xs text-ink/50 underline"
      >
        Didn&rsquo;t open? Show the number and message
      </button>
      <p className="mt-3 text-center text-xs text-ink/50">
        The code works for 5 minutes.
      </p>
    </div>
  );
}

// Fallback when the deep link didn't open an app — desktop (sms: can't launch
// an app there) or a blocked in-app webview. Show the number + prefilled text
// to copy, a real sms: link, and (in FB/IG/TikTok webviews) a nudge to reopen
// in a real browser. The poller signs them in once the text lands.
function HandoffFallback(p: {
  handoff: { to_number: string; greeting: string };
  ctaSecondary: string;
}) {
  return (
    <div className="ori-modal-content px-6 pb-8 pt-10 sm:px-8">
      <h2
        id="message-ori-title"
        className="ori-modal-title mb-2 text-3xl font-medium tracking-tight"
        style={{ fontFamily: serif, color: '#3A331D' }}
      >
        Send this to Ori
      </h2>
      <p className="ori-modal-subtitle mb-6 text-sm text-ink/70">
        Text the message below to that number — you&rsquo;ll be signed in here
        automatically. Once you send it, Ori replies in Messages, so keep the
        conversation going right there.
      </p>

      {isInAppWebview() && (
        <div
          className="mb-5 rounded-xl border border-oriOrange/30 bg-oriOrange/5 px-4 py-3 text-sm text-ink/80"
          role="note"
        >
          In the Facebook or Instagram app? Tap <span className="font-semibold">⋯</span>{' '}
          (top-right) and choose <span className="font-semibold">Open in Safari/Chrome</span>,
          then tap Open Messages again.
        </div>
      )}

      <div className="rounded-2xl bg-white p-5" style={{ boxShadow: '0 2px 14px rgba(58,51,29,0.08)' }}>
        <NumberRow number={formatPhone(p.handoff.to_number)} rawNumber={p.handoff.to_number} />
        <div className="my-4 h-px bg-ink/10" aria-hidden />
        <GreetingBubble text={p.handoff.greeting} />
      </div>

      <OpenMessagesCta
        href={buildSmsHref(p.handoff.to_number, p.handoff.greeting)}
        onClick={trackOpenMessages}
        primary="Open Messages"
        secondary={p.ctaSecondary}
      />
      <p className="mt-3 text-center text-xs text-ink/50">
        The code works for 5 minutes.
      </p>
    </div>
  );
}

// Signed-in viewer tapped "Message Ori" — no onboarding, just back to their
// thread. On mobile App.tsx already deep-linked away before opening this; this
// is the desktop sheet (sms: can't launch an app there) with the number to copy.
function ReturningStage({ oriNumber }: { oriNumber: string }) {
  const smsHref = oriNumber ? buildSmsHref(oriNumber, '') : '#';
  return (
    <div className="ori-modal-content px-6 pb-8 pt-10 sm:px-8">
      <p className="mb-1 text-xs uppercase tracking-[0.18em] text-ink/50" style={{ fontFamily: sans }}>
        Welcome back
      </p>
      <h2 className="ori-modal-title mb-2 text-3xl font-medium tracking-tight" style={{ fontFamily: serif, color: '#3A331D' }}>
        Open Ori
      </h2>
      <p className="ori-modal-subtitle mb-6 text-sm text-ink/70">
        You&rsquo;re already signed in — pick up right where you left off. On your
        phone, tap below; on a computer, text Ori at the number below.
      </p>

      {oriNumber && (
        <div className="mb-5 rounded-2xl bg-white p-5" style={{ boxShadow: '0 2px 14px rgba(58,51,29,0.08)' }}>
          <NumberRow number={formatPhone(oriNumber)} rawNumber={oriNumber} />
        </div>
      )}

      <OpenMessagesCta
        href={smsHref}
        onClick={trackOpenMessages}
        primary="Open Messages"
        secondary="back to your chat with Ori"
        className="mt-1"
      />
    </div>
  );
}

// The poll saw the code land — this tab confirms instead of staring at the join
// screen forever (the only web-side confirmation desktop signups get). CTA opens
// the user's REAL Ori thread, no code body.
function VerifiedStage({ oriNumber }: { oriNumber: string }) {
  const smsHref = oriNumber ? buildSmsHref(oriNumber, '') : '#';
  return (
    <div className="ori-modal-content px-6 pb-8 pt-10 sm:px-8">
      <p className="mb-1 text-xs uppercase tracking-[0.18em] text-ink/50" style={{ fontFamily: sans }}>
        All set
      </p>
      <h2 className="ori-modal-title mb-2 text-3xl font-medium tracking-tight" style={{ fontFamily: serif, color: '#3A331D' }}>
        You&rsquo;re in 🎉
      </h2>
      <p className="ori-modal-subtitle mb-6 text-sm text-ink/70">
        Ori got your text — everything happens in Messages from here. Ori&rsquo;s already saying hi.
      </p>
      <OpenMessagesCta
        href={smsHref}
        primary="Open Messages"
        secondary="pick up the conversation"
        className="mt-1"
      />
    </div>
  );
}

// The 5-minute code died before the text arrived. Don't strand them —
// one tap restarts the form with a fresh nonce → fresh code.
function ExpiredStage({ onRestart }: { onRestart: () => void }) {
  return (
    <div className="ori-modal-content px-6 pb-8 pt-10 sm:px-8">
      <p className="mb-1 text-xs uppercase tracking-[0.18em] text-ink/50" style={{ fontFamily: sans }}>
        One more try
      </p>
      <h2 className="ori-modal-title mb-2 text-3xl font-medium tracking-tight" style={{ fontFamily: serif, color: '#3A331D' }}>
        That code expired
      </h2>
      <p className="ori-modal-subtitle mb-6 text-sm text-ink/70">
        Codes only live 5 minutes. Grab a fresh one — takes 10 seconds.
      </p>
      <button
        type="button"
        onClick={onRestart}
        className="flex h-12 w-full items-center justify-center rounded-full text-base font-semibold text-cream transition-opacity"
        style={{ background: '#F4720C', border: '2px solid #FBAB6C', fontFamily: sans }}
      >
        Get a fresh code
      </button>
    </div>
  );
}

function CopyPill({
  text,
  label = 'Copy',
}: {
  text: string;
  label?: string;
}) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      className="rounded-full px-4 py-1.5 text-sm font-medium text-ink/70 hover:bg-ink/10"
      style={{ background: 'rgba(58,51,29,0.06)' }}
    >
      {copied ? 'Copied!' : label}
    </button>
  );
}

function NumberRow({ number, rawNumber }: { number: string; rawNumber: string }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className="text-xs font-semibold uppercase tracking-[0.2em] text-ink/40"
        style={{ fontFamily: sans }}
      >
        TO
      </span>
      <span
        className="flex-1 truncate text-xl font-semibold text-ink"
        style={{ fontFamily: sans }}
      >
        {number}
      </span>
      <CopyPill text={rawNumber} />
    </div>
  );
}

function GreetingBubble({ text }: { text: string }) {
  return (
    <div>
      <div
        className="inline-block max-w-full rounded-2xl px-4 py-2.5"
        style={{ background: '#F2E5D0', color: '#3A331D' }}
      >
        <span
          style={{
            fontFamily: '"SF Compact Display", -apple-system, system-ui, sans-serif',
            fontSize: 16,
          }}
        >
          {text}
        </span>
      </div>
      <div className="mt-3">
        <CopyPill text={text} label="Copy message" />
      </div>
    </div>
  );
}

function ChatBubbleGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 2C6.48 2 2 5.97 2 10.5c0 2.39 1.27 4.54 3.31 6.05-.13.95-.62 2.4-1.79 3.79-.21.24-.05.61.27.58 2.3-.21 4.18-.97 5.43-1.7 0.92.18 1.86.28 2.78.28 5.52 0 10-3.97 10-8.5S17.52 2 12 2z" />
    </svg>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string | null;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <label className="ori-modal-label mb-1 block text-sm font-medium text-ink/80">{label}</label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-ink/50">{hint}</p>}
      {error && (
        <p className="mt-1 text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
